import React, { useState, useEffect, useCallback } from 'react'
import { Card, Button, Input, Select, Tag, Typography, message, Space, Popconfirm, Checkbox, Divider, Row, Col } from 'antd'
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons'
import { backend } from '../../api'
import { FEATURE_DEFS, getDefaultFeaturesForLevel } from '../../checkFeature'

const { Title, Text } = Typography

const LEVEL_COLORS = { basic: 'blue', vip: 'gold', pro: 'geekblue', admin: 'red' }

const LEVEL_PRESETS = [
  { level: 'basic', name: '基础版', price: '免费', features: getDefaultFeaturesForLevel('basic') },
  { level: 'vip', name: '会员版', price: '99元/月', features: getDefaultFeaturesForLevel('vip') },
  { level: 'pro', name: '专业版', price: '299元/月', features: getDefaultFeaturesForLevel('pro') }
]

export default function MembershipPlans() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const loadPlans = useCallback(async () => {
    setLoading(true)
    try {
      const res = await backend.adminGetMembershipPlans()
      if (res.success) setPlans(res.plans || [])
    } catch (e) { message.error('加载失败') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadPlans() }, [loadPlans])

  const addPlan = () => {
    setPlans([...plans, { level: '', name: '', price: '', features: [] }])
  }

  const removePlan = (index) => {
    setPlans(plans.filter((_, i) => i !== index))
  }

  const updatePlan = (index, field, value) => {
    const next = [...plans]
    next[index] = { ...next[index], [field]: value }
    setPlans(next)
  }

  const toggleFeature = (planIndex, featureKey) => {
    const next = [...plans]
    const plan = { ...next[planIndex] }
    const features = [...(plan.features || [])]
    if (features.includes(featureKey)) {
      plan.features = features.filter(f => f !== featureKey)
    } else {
      plan.features = [...features, featureKey]
    }
    next[planIndex] = plan
    setPlans(next)
  }

  const applyPreset = (index, preset) => {
    const next = [...plans]
    next[index] = { ...preset }
    setPlans(next)
  }

  const handleSave = async () => {
    for (let i = 0; i < plans.length; i++) {
      if (!plans[i].level || !plans[i].name) {
        message.warning(`第 ${i + 1} 个方案的等级标识和名称不能为空`)
        return
      }
    }
    setSaving(true)
    try {
      const res = await backend.adminUpdateMembershipPlans(plans)
      if (res.success) {
        message.success('会员方案已保存')
        loadPlans()
      } else {
        message.error(res.message || '保存失败')
      }
    } catch (e) { message.error('保存失败') }
    finally { setSaving(false) }
  }

  const handleResetDefaults = () => {
    setPlans(LEVEL_PRESETS.map(p => ({ ...p, features: [...p.features] })))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>会员方案配置</Title>
        <Space>
          <Button onClick={handleResetDefaults}>恢复默认方案</Button>
          <Button icon={<PlusOutlined />} onClick={addPlan}>新增方案</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>保存</Button>
        </Space>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plans.map((plan, i) => (
          <Card key={i} size="small" title={
            <Space>
              <Text strong>方案 {i + 1}</Text>
              {plan.level && <Tag color={LEVEL_COLORS[plan.level] || 'default'}>{plan.level}</Tag>}
            </Space>
          } extra={
            <Space>
              <Select size="small" style={{ width: 100 }} placeholder="快速预设"
                value={undefined} onChange={v => { const p = LEVEL_PRESETS.find(x => x.level === v); if (p) applyPreset(i, p) }}
                options={LEVEL_PRESETS.map(p => ({ value: p.level, label: p.name }))} />
              <Popconfirm title="确定删除此方案?" onConfirm={() => removePlan(i)}>
                <Button type="text" danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            </Space>
          }>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>等级标识</Text>
                  <Select value={plan.level || undefined} onChange={v => updatePlan(i, 'level', v)}
                    style={{ width: '100%' }} placeholder="如 basic, vip, pro"
                    options={[{ value: 'basic', label: 'basic (基础)' }, { value: 'vip', label: 'vip (会员)' }, { value: 'pro', label: 'pro (专业)' }, { value: 'enterprise', label: 'enterprise (企业)' }]} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>方案名称</Text>
                  <Input value={plan.name} onChange={e => updatePlan(i, 'name', e.target.value)} placeholder="如 基础版" />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>价格</Text>
                  <Input value={plan.price} onChange={e => updatePlan(i, 'price', e.target.value)} placeholder="如 99元/月" />
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>功能权限（勾选即启用）</Text>
              <Row gutter={[8, 4]} style={{ marginTop: 4 }}>
                {FEATURE_DEFS.map(fd => (
                  <Col span={8} key={fd.key}>
                    <Checkbox
                      checked={(plan.features || []).includes(fd.key)}
                      onChange={() => toggleFeature(i, fd.key)}
                    >
                      <Text style={{ fontSize: 13 }}>{fd.label}</Text>
                    </Checkbox>
                    <div style={{ fontSize: 11, color: '#999', marginLeft: 24 }}>{fd.desc}</div>
                  </Col>
                ))}
              </Row>
            </Space>
          </Card>
        ))}
      </div>
    </div>
  )
}
